import { z } from 'zod'

import { OPTION_LABELS, type Option } from '@/features/options/labels'

/** The states and territories a listing can be in, as the API names them. */
export const STATES = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'] as const

/** The registered market sources a member can narrow to: Carsales first (ADR 0002). */
export const SOURCES = [{ key: 'carsales', name: 'Carsales' }] as const

/** The year ranges the Year range select offers, by the key the address carries. */
export const YEAR_RANGES = {
  '2024-': { label: '2024 and newer', min: 2024, max: undefined },
  '2020-2023': { label: '2020 to 2023', min: 2020, max: 2023 },
  '2015-2019': { label: '2015 to 2019', min: 2015, max: 2019 },
  '-2014': { label: '2014 and older', min: undefined, max: 2014 },
} as const satisfies Record<string, { label: string; min?: number; max?: number }>

/** The most km the Km select offers, by the key the address carries. */
export const KM_LIMITS = {
  '20000': { label: 'Under 20,000 km', max: 20_000 },
  '50000': { label: 'Under 50,000 km', max: 50_000 },
  '100000': { label: 'Under 100,000 km', max: 100_000 },
  '150000': { label: 'Under 150,000 km', max: 150_000 },
} as const

/** A listing's own build, as the API names it (the vehicles' vocabulary). */
export const TRANSMISSIONS = ['automatic', 'manual'] as const
export const FUELS = ['petrol', 'diesel', 'hybrid', 'plug_in_hybrid', 'electric', 'lpg'] as const
export const BODIES = [
  'sedan',
  'hatch',
  'wagon',
  'suv',
  'ute',
  'cab_chassis',
  'coupe',
  'convertible',
  'van',
  'people_mover',
] as const

/** Who put a listing up, once the source said. */
export const SELLERS = { private: 'Private', dealer: 'Dealer' } as const

/** The columns the table sorts by, and the API's name for each. */
export const SORTS = {
  posted: 'first_seen_at',
  vehicle: 'title',
} as const

/** Every tab the address can name, as the API names its status. */
export const MARKET_TAB_KEYS = ['on_market', 'removed', 'sold', 'unmatched'] as const

export type MarketTab = (typeof MARKET_TAB_KEYS)[number]

/**
 * The Market's top tabs, in order, and their labels: on the market, and every removal whatever
 * it concluded.
 */
export const MARKET_TABS = {
  on_market: 'On market',
  removed: 'Off market',
} as const satisfies Partial<Record<MarketTab, string>>

export type TopMarketTab = keyof typeof MARKET_TABS

/**
 * The tabs under each top tab, in order, and their labels: all of it first, then the part of it
 * worth seeing alone, which the All count includes. Unmatched is the listings on the market whose
 * page was read but no vehicle could be keyed, where the normaliser fails; Sold is the removals
 * concluded sold.
 */
export const SUB_TABS = {
  on_market: { on_market: 'All', unmatched: 'Unmatched' },
  removed: { removed: 'All', sold: 'Sold' },
} as const satisfies Record<TopMarketTab, Partial<Record<MarketTab, string>>>

/** The top tab a tab sits under: Unmatched under On market, Sold under Off market. */
export function topTabOf(tab: MarketTab): TopMarketTab {
  if (tab === 'unmatched') return 'on_market'
  if (tab === 'sold') return 'removed'
  return tab
}

const vehicleWord = z
  .string()
  .trim()
  .max(60)
  .transform((word) => word || undefined)
  .optional()
  .catch(undefined)

/** A whole number a filter bounds by, from the address; anything else leaves it off. */
const bound = (most: number) => z.coerce.number().int().min(0).max(most).optional().catch(undefined)

/**
 * Several values of a filter, any one of which lets a listing by. One value alone (an address
 * from before a filter took several) is read as a list of one; none is the filter off.
 */
const some = <T extends z.ZodType>(item: T) =>
  z
    .preprocess(
      (value) => (value === undefined ? undefined : Array.isArray(value) ? value : [value]),
      z.array(item).max(100),
    )
    .transform((values) => (values === undefined || values.length === 0 ? undefined : values))
    .optional()
    .catch(undefined)

const keyOf = <T extends Record<string, unknown>>(record: T) =>
  z.enum(Object.keys(record) as [keyof T & string, ...(keyof T & string)[]])

/**
 * The Market screen's search: the words searched for, the filters, the sort and the page, in the
 * address so a filtered list can be bookmarked or shared. Anything unreadable is dropped rather
 * than refused, which leaves that filter off.
 */
export const marketSearchSchema = z.object({
  q: z
    .string()
    .trim()
    .max(200)
    .transform((q) => q || undefined)
    .optional()
    .catch(undefined),
  /** The make of the vehicle a listing is keyed to, as the makes list names it ("BMW"). */
  make: vehicleWord,
  /** One of that make's models ("X5"); only with a make. */
  model: vehicleWord,
  /** Some of that model's badges ("xDrive30d M Sport"): any one of them; only with a model. */
  badge: some(z.string().trim().min(1).max(120)),
  /** Series a title states ("MY21"): any one of them; only with a model. */
  series: some(z.string().trim().min(1).max(40)),
  year: keyOf(YEAR_RANGES).optional().catch(undefined),
  km: keyOf(KM_LIMITS).optional().catch(undefined),
  /**
   * Exact bounds, as a plain-language search reads them ("2018 to 2020", "under 40k"): the
   * build years, the km, and the asking price in cents. A year or km bound stands in for the
   * Year range or Km select's.
   */
  year_min: bound(2100),
  year_max: bound(2100),
  km_min: bound(2_000_000),
  km_max: bound(2_000_000),
  price_min: bound(1_000_000_000),
  price_max: bound(1_000_000_000),
  state: z.enum(STATES).optional().catch(undefined),
  /** The listing's own build: a listing whose title never said is let by none of these. */
  transmission: z.enum(TRANSMISSIONS).optional().catch(undefined),
  fuel: z.enum(FUELS).optional().catch(undefined),
  body: z.enum(BODIES).optional().catch(undefined),
  /** Private or dealer listings only. Not on Cars by spec. */
  seller: keyOf(SELLERS).optional().catch(undefined),
  /** Options the listings' pages say are fitted (ticket 18): only cars with every one. */
  option: some(z.enum(Object.keys(OPTION_LABELS) as [Option, ...Option[]])),
  source: z
    .enum(SOURCES.map((source) => source.key))
    .optional()
    .catch(undefined),
  /** A column of `SORTS`, `-` first for newest or last first. Posted, newest first by default. */
  sort: z.enum(['posted', '-posted', 'vehicle', '-vehicle']).optional().catch(undefined),
  /** The Market tab; On market when the address names none. Not on Cars by spec. */
  tab: z.enum(MARKET_TAB_KEYS).optional().catch(undefined),
  /**
   * `cars` for Cars by spec (every exact car with its figures, ticket 16); every listing one by
   * one when the address names none.
   */
  view: z.enum(['cars']).optional().catch(undefined),
  /** Cars by spec's sort: a key of `SPEC_SORTS`, `-` first for most first. */
  car_sort: z
    .enum(['live', '-live', 'sold', '-sold', 'asking', '-asking', 'days', '-days', 'car', '-car'])
    .optional()
    .catch(undefined),
  page: z.coerce.number().int().min(1).optional().catch(undefined),
})

export type MarketSearch = z.output<typeof marketSearchSchema>

/** The filters of a search, without its page and sort: what the filter bar sets. */
export type MarketFilters = Pick<
  MarketSearch,
  | 'q'
  | 'make'
  | 'model'
  | 'badge'
  | 'series'
  | 'year'
  | 'km'
  | 'year_min'
  | 'year_max'
  | 'km_min'
  | 'km_max'
  | 'price_min'
  | 'price_max'
  | 'state'
  | 'transmission'
  | 'fuel'
  | 'body'
  | 'seller'
  | 'option'
  | 'source'
>

/** Every filter off: what a new plain-language search starts from. */
export const NO_FILTERS: Record<keyof MarketFilters, undefined> = {
  q: undefined,
  make: undefined,
  model: undefined,
  badge: undefined,
  series: undefined,
  year: undefined,
  km: undefined,
  year_min: undefined,
  year_max: undefined,
  km_min: undefined,
  km_max: undefined,
  price_min: undefined,
  price_max: undefined,
  state: undefined,
  transmission: undefined,
  fuel: undefined,
  body: undefined,
  seller: undefined,
  option: undefined,
  source: undefined,
}

/** The build years a search lets by: its exact years, else its Year range's. */
export function yearsOf(search: MarketFilters): { min?: number; max?: number } {
  if (search.year_min !== undefined || search.year_max !== undefined) {
    return { min: search.year_min, max: search.year_max }
  }
  return search.year === undefined ? {} : YEAR_RANGES[search.year]
}

/** The km a search lets by: its exact km, else its Km select's. */
export function kmOf(search: MarketFilters): { min?: number; max?: number } {
  if (search.km_min !== undefined || search.km_max !== undefined) {
    return { min: search.km_min, max: search.km_max }
  }
  return search.km === undefined ? {} : { max: KM_LIMITS[search.km].max }
}
